import { useEffect, useRef, useState } from "react";

import { postAutocomplete } from "../api/client";
import type { AutocompleteSuggestion } from "../types/api";
import { formatPrice, parsePriceInput } from "../utils/price";

interface Props {
  onSubmit: (
    address: string,
    askingPrice: number | null,
    placeId: string | null,
  ) => void;
  /**
   * Paid tier requires a price (it drives the offer range), so the label
   * drops "(optional)" and submission is blocked without one. Free tier
   * leaves price optional — the chat finds the listing price via web search.
   */
  priceRequired?: boolean;
  /** Pre-seed the address + place_id (Pattern C — carry property post-upgrade). */
  initialAddress?: string;
  initialPlaceId?: string | null;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY = 3;

export function ScoreForm({
  onSubmit,
  priceRequired = false,
  initialAddress = "",
  initialPlaceId = null,
}: Props) {
  const [address, setAddress] = useState(initialAddress);
  const [priceText, setPriceText] = useState("$");
  const [priceNumeric, setPriceNumeric] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Cached place_id from the autocomplete selection (Req 16). Cleared
  // whenever the user edits the address text — a manual edit means the
  // cached place_id no longer matches what's in the input box, so the
  // backend should fall back to SearchPlaceIndexForText.
  const [placeId, setPlaceId] = useState<string | null>(initialPlaceId);
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Set true when the user picks a suggestion. The selection calls
  // setAddress(s.address), which re-triggers this effect — without this
  // guard the effect would refetch and reopen the dropdown, which read as
  // a "double-click needed to select" bug (Task 9.9a autocomplete fix).
  // Initialized true on a prefilled address (Pattern C) so the dropdown
  // doesn't auto-open on mount with a place_id already chosen.
  const suppressFetchRef = useRef(Boolean(initialAddress && initialPlaceId));

  useEffect(() => {
    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    if (address.trim().length < MIN_QUERY) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      postAutocomplete(address)
        .then((r) => {
          setSuggestions(r.suggestions);
          setShowSuggestions(true);
        })
        .catch(() => setSuggestions([]));
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [address]);

  // Sync when a prefill arrives AFTER mount (e.g., App reads the saved/upgrade
  // property from sessionStorage a tick later). useState's initial value only
  // applies on first render, so without this the input stayed empty while the
  // hint showed the address → "Address is required" on submit.
  useEffect(() => {
    if (initialAddress) {
      suppressFetchRef.current = true; // don't reopen the dropdown for a known address
      setAddress(initialAddress);
    }
    if (initialPlaceId) setPlaceId(initialPlaceId);
  }, [initialAddress, initialPlaceId]);

  function handlePriceBlur() {
    // Parse + format on blur so the user sees the canonical value
    const parsed = parsePriceInput(priceText);
    if (parsed !== null) {
      setPriceNumeric(parsed);
      setPriceText(formatPrice(parsed));
    } else if (priceText.trim() === "") {
      setPriceNumeric(null);
    }
  }

  function handlePriceFocus() {
    // Show "$" + raw digits when focused (easier to fix small typos
    // and matches the leading-$ convention from the initial state)
    if (priceNumeric !== null) {
      setPriceText("$" + String(priceNumeric));
    } else if (!priceText) {
      setPriceText("$");
    }
  }

  function handlePriceChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPriceText(e.target.value);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setValidationError(null);

    const trimmedAddress = address.trim();
    if (!trimmedAddress) {
      setValidationError("Address is required.");
      return;
    }
    // Req 9.9a: require a place_id from autocomplete (not raw typed text) so
    // the backend always has a resolvable property and never errors on a
    // free-text address it can't match.
    if (!placeId) {
      setValidationError("Select your address from the dropdown suggestions.");
      return;
    }

    // Asking price is optional on the free tier (the chat finds the listing
    // price via web search) but required on paid (it drives the offer range).
    // A non-empty price must always be a positive number.
    const raw = priceText.replace(/^\$/, "").trim();
    let parsed: number | null = null;
    if (raw !== "") {
      parsed = priceNumeric ?? parsePriceInput(priceText);
      if (parsed === null || parsed <= 0) {
        setValidationError("If you enter an asking price, it must be a positive number.");
        return;
      }
    }
    if (priceRequired && parsed === null) {
      setValidationError("Enter the asking price so we can show your offer range.");
      return;
    }

    onSubmit(trimmedAddress, parsed, placeId);
  }

  return (
    <form className="score-form" onSubmit={handleSubmit}>
      <label>
        Property address
        <input
          type="text"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            // A manual edit invalidates the autocomplete-cached place_id;
            // submit will fall back to the backend SearchPlaceIndexForText.
            setPlaceId(null);
          }}
          autoComplete="off"
          aria-label="Property address"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="autocomplete-dropdown" aria-label="Address suggestions">
            {suggestions.map((s) => (
              <li key={s.place_id}>
                <button
                  type="button"
                  onClick={() => {
                    suppressFetchRef.current = true;
                    setAddress(s.address);
                    setPlaceId(s.place_id);
                    setSuggestions([]);
                    setShowSuggestions(false);
                    setValidationError(null);
                  }}
                >
                  {s.address}
                </button>
              </li>
            ))}
          </ul>
        )}
      </label>

      <label>
        Asking price{" "}
        {!priceRequired && <span className="field-optional">(optional)</span>}
        <input
          type="text"
          inputMode="decimal"
          value={priceText}
          onChange={handlePriceChange}
          onBlur={handlePriceBlur}
          onFocus={handlePriceFocus}
          aria-label="Asking price"
          aria-describedby="price-hint"
        />
        <span id="price-hint" className="field-hint">
          Shortcuts: <code>499</code> → <code>$499,000</code>,{" "}
          <code>500k</code> → <code>$500,000</code>,{" "}
          <code>1.2m</code> → <code>$1,200,000</code>.
        </span>
      </label>

      {validationError && <p className="error">{validationError}</p>}

      <button type="submit" className="submit">
        Score this property
      </button>
    </form>
  );
}
